import { useState, useEffect, useCallback } from 'react';
import { Agency } from '../types/agency';
import { agencyAPI, CreateAgencyData, UpdateAgencyData } from '../api/agencies';

export const useAgencies = () => {
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAgencies = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await agencyAPI.getAll();
        console.log('📥 useAgencies - Données brutes reçues de l\'API:', data);
        if (data.length > 0) {
          console.log('📥 useAgencies - Première agence brand_color:', data[0].brand_color);
        }

        // Transformer les données pour correspondre au type Agency
        const transformedAgencies = data.map((agency: any) => ({
          id: agency.id.toString(),
          name: agency.name,
          address: agency.address,
          city: agency.city,
          wilaya: agency.wilaya,
          phone: agency.contact_phone,
          email: agency.contact_email,
          website_url: agency.website_url,
          logo_url: agency.logo_url,
          brand_color: agency.brand_color,
          status: agency.status,
          subscription: {
            plan: agency.subscription_plan,
            startDate: new Date(),
            endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 an
          },
          suspensionReason: agency.suspension_reason,
        }));

        console.log('📥 useAgencies - Agences transformées:', transformedAgencies);
        if (transformedAgencies.length > 0) {
          console.log('📥 useAgencies - Première agence transformée brand_color:', transformedAgencies[0].brand_color);
        }

        setAgencies(transformedAgencies);
      } catch (error) {
        console.error('Failed to fetch agencies:', error);
        setError('Erreur lors du chargement des agences');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgencies();
  }, []);

  const createAgency = useCallback(async (agencyData: CreateAgencyData | FormData) => {
    setError(null);
    try {
      console.log('🏭 useAgencies.createAgency - Type de données:', agencyData instanceof FormData ? 'FormData' : 'Object');
      const createdAgency = await agencyAPI.create(agencyData);
      console.log('✅ useAgencies.createAgency - Agence créée:', createdAgency);
      const transformedAgency = {
        id: createdAgency.id.toString(),
        name: createdAgency.name,
        address: createdAgency.address,
        city: createdAgency.city,
        wilaya: createdAgency.wilaya,
        phone: createdAgency.contact_phone,
        email: createdAgency.contact_email,
        website_url: createdAgency.website_url,
        logo_url: createdAgency.logo_url,
        brand_color: createdAgency.brand_color || '#0a8345',
        status: createdAgency.status,
        subscription: {
          plan: createdAgency.subscription_plan,
          startDate: new Date(),
          endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
        },
      };
      setAgencies(prev => [transformedAgency, ...prev]);
      return true;
    } catch (error) {
      console.error('❌ useAgencies.createAgency - Erreur:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors de la création de l\'agence');
      return false;
    }
  }, []);

  const updateAgency = useCallback(async (id: string, agencyData: UpdateAgencyData | FormData) => {
    setError(null);
    try {
      console.log('🔄 useAgencies.updateAgency - ID:', id, 'Type:', agencyData instanceof FormData ? 'FormData' : 'Object');
      const updatedAgency = await agencyAPI.update(id, agencyData);
      console.log('✅ useAgencies.updateAgency - Agence mise à jour:', updatedAgency);

      setAgencies(prev => prev.map(agency => {
        if (agency.id === id) {
          return {
            ...agency,
            name: updatedAgency.name || agency.name,
            address: updatedAgency.address || agency.address,
            city: updatedAgency.city || agency.city,
            wilaya: updatedAgency.wilaya || agency.wilaya,
            phone: updatedAgency.contact_phone || agency.phone,
            email: updatedAgency.contact_email || agency.email,
            website_url: updatedAgency.website_url || agency.website_url,
            logo_url: updatedAgency.logo_url || agency.logo_url,
            brand_color: updatedAgency.brand_color || agency.brand_color,
            status: updatedAgency.status || agency.status,
            subscription: {
              ...agency.subscription,
              plan: updatedAgency.subscription_plan || agency.subscription.plan,
            },
            suspensionReason: updatedAgency.suspension_reason || agency.suspensionReason,
          };
        }
        return agency;
      }));
      return true;
    } catch (error) {
      console.error('❌ useAgencies.updateAgency - Erreur:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors de la mise à jour de l\'agence');
      return false;
    }
  }, []);

  const deleteAgency = useCallback(async (id: string) => {
    setError(null);
    try {
      await agencyAPI.delete(id);
      setAgencies(prev => prev.filter(agency => agency.id !== id));
      return true;
    } catch (error) {
      console.error('Failed to delete agency:', error);
      setError('Erreur lors de la suppression de l\'agence');
      return false;
    }
  }, []);

  const suspendAgency = useCallback(async (id: string, reason: string) => {
    setError(null);
    try {
      await agencyAPI.suspend(id, reason);
      setAgencies(prev => prev.map(agency => 
        agency.id === id 
          ? { ...agency, status: 'suspended', suspensionReason: reason }
          : agency
      ));
      return true;
    } catch (error) {
      console.error('Failed to suspend agency:', error);
      setError('Erreur lors de la suspension de l\'agence');
      return false;
    }
  }, []);

  const reactivateAgency = useCallback(async (id: string) => {
    setError(null);
    try {
      await agencyAPI.reactivate(id);
      setAgencies(prev => prev.map(agency => 
        agency.id === id 
          ? { ...agency, status: 'active', suspensionReason: undefined }
          : agency
      ));
      return true;
    } catch (error) {
      console.error('Failed to reactivate agency:', error);
      setError('Erreur lors de la réactivation de l\'agence');
      return false;
    }
  }, []);

  return {
    agencies,
    isLoading,
    error,
    createAgency,
    updateAgency,
    deleteAgency,
    suspendAgency,
    reactivateAgency,
  };
};